import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";
import dayjs from "dayjs";
import api from "../../../utils/Url";
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';

interface Notice {
  serialNo: number;
  noticeID: number;
  notice: string;
  noticeLink: string;
  noticeDate: string;
  noticeDoc: string;
  formattedNoticeDate: string;
  fromDate: string;
  toDate: string;
}

export default function NoticeBoard() {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewedNotice, setViewedNotice] = useState<{
    image: string,
    name: string,
    isPdf: boolean
  } | null>(null);

  useEffect(() => {
    fetchNoticesData();
  }, []);

  const fetchNoticesData = async () => {
    try {
      const ID = localStorage.getItem("useR_ID")?.replace(/\D/g, '') || "0";
      const collectData = {
        noticeID: -1,
        user_ID: parseInt(ID),
        isActive: true,
      };

      const response = await api.post(`Notice/GetNoticeMaster`, collectData);
      const data = response.data.data;

      const noticesWithIds = data.map((notice: any, index: number) => ({
        ...notice,
        serialNo: index + 1,
        formattedNoticeDate: dayjs(notice.noticeDate).format("DD/MM/YYYY"),
      }));

      setNotices(noticesWithIds);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching notices:", error);
      setIsLoading(false);
    }
  };

  const handleNoticeClick = async (noticeId: number, noticeName: string) => {
    try {
      setIsLoading(true);
      const ID = localStorage.getItem("useR_ID")?.replace(/\D/g, '') || "0";
      const collectData = {
        noticeID: noticeId,
        user_ID: parseInt(ID),
        isActive: true,
      };

      const response = await api.post(`Notice/GetNoticeMaster`, collectData);

      if (response.data.isSuccess && response.data.data.length > 0) {
        const noticeData = response.data.data[0];
        if (noticeData.noticeDoc) {
          // Check if the document is a PDF (either by extension or base64 prefix)
          const isPdf = noticeData.noticeDoc.startsWith('data:application/pdf') ||
            noticeData.noticeDoc.toLowerCase().endsWith('.pdf');

          setViewedNotice({
            image: noticeData.noticeDoc,
            name: noticeName,
            isPdf: isPdf
          });
        } else {
          console.error("No notice document available");
        }
      } else {
        console.error("Failed to fetch notice document");
      }
    } catch (error) {
      console.error("Error fetching notice document:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    if (viewedNotice) {
      const link = document.createElement('a');
      link.href = viewedNotice.image;
      link.download = viewedNotice.name + (viewedNotice.isPdf ? '.pdf' : '');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {notices.length === 0 ? (
        <Typography variant="body1" align="center">
          No notices available
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: "100%", boxShadow: "none" }}>
          <Table stickyHeader size="small">
            {/* <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Sr. No.</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Notice</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
              </TableRow>
            </TableHead> */}
            <TableBody>
              {notices.map((notice) => (
                <TableRow key={notice.noticeID}>
                  {/* <TableCell>{notice.serialNo}</TableCell> */}
                  <TableCell>
                    <Button
                      variant="text"
                      onClick={() => handleNoticeClick(notice.noticeID, notice.notice)}
                      sx={{
                        textTransform: "none",
                        justifyContent: "flex-start",
                        padding: 0,
                        width: "100%"
                      }}
                    >
                      <Box textAlign="left">
                        <Typography fontWeight="bold"
                        sx={{
                            color: "#d20e0e",
                            textDecoration: "underline"
                          }}>
                          {notice.notice} |{" "}
                          {dayjs(notice.fromDate).format("DD/MM/YYYY")} -{" "}
                          {dayjs(notice.toDate).format("DD/MM/YYYY")}
                        </Typography>

                        <Typography
                          sx={{
                            color: "#2130fc",
                            textDecoration: "underline"
                          }}
                        >
                          {notice.noticeLink}
                        </Typography>
                      </Box>
                    </Button>
                  </TableCell>
                  {/* <TableCell>{notice.formattedNoticeDate}</TableCell> */}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Enhanced Document Viewer Modal */}
      {viewedNotice && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          padding: '20px',
          boxSizing: 'border-box'
        }} onClick={() => setViewedNotice(null)}>
          <div style={{
            width: '100%',
            maxWidth: '90vw',
            height: '90vh',
            backgroundColor: 'white',
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }} onClick={e => e.stopPropagation()}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px',
              backgroundColor: '#f5f5f5',
              borderBottom: '1px solid #e0e0e0'
            }}>
              <Typography variant="h6" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {viewedNotice.name}
              </Typography>
              <div>
                <IconButton
                  color="primary"
                  onClick={handleDownload}
                  aria-label="download"
                  style={{ marginRight: '10px' }}
                >
                  <DownloadIcon />
                </IconButton>
                <IconButton
                  color="error"
                  onClick={() => setViewedNotice(null)}
                  aria-label="close"
                >
                  <CloseIcon />
                </IconButton>
              </div>
            </div>
            <div style={{
              flex: 1,
              overflow: 'auto',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '20px'
            }}>
              {viewedNotice.isPdf ? (
                <iframe
                  src={viewedNotice.image}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                  title={viewedNotice.name}
                />
              ) : (
                <img
                  src={viewedNotice.image}
                  alt={viewedNotice.name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </Box>
  );
}








// import React, { useEffect, useState } from "react";
// import {
//   Box,
//   Typography,
//   Button,
//   CircularProgress,
//   Paper,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
// } from "@mui/material";
// import dayjs from "dayjs";
// import api from "../../../utils/Url";

// interface Notice {
//   serialNo: number;
//   noticeID: number;
//   notice: string;
//   noticeLink: string;
//   noticeDate: string;
//   noticeDoc: string;
//   formattedNoticeDate: string;
// }

// export default function NoticeBoard() {
//   const [notices, setNotices] = useState<Notice[]>([]);
//   const [isLoading, setIsLoading] = useState(true);

//   useEffect(() => {
//     fetchNoticesData();
//   }, []);

//   const fetchNoticesData = async () => {
//     try {
//       const ID = localStorage.getItem("useR_ID")?.replace(/\D/g, '') || "0";
//       const collectData = {
//         noticeID: -1,
//         user_ID: parseInt(ID),
//         isActive: true,
//       };

//       const response = await api.post(`Notice/GetNoticeMaster`, collectData);
//       const data = response.data.data;

//       const noticesWithIds = data.map((notice: any, index: number) => ({
//         ...notice,
//         serialNo: index + 1,
//         formattedNoticeDate: dayjs(notice.noticeDate).format("DD/MM/YYYY"),
//       }));

//       setNotices(noticesWithIds);
//       setIsLoading(false);
//     } catch (error) {
//       console.error("Error fetching notices:", error);
//       setIsLoading(false);
//     }
//   };

//   const handleNoticeClick = async (noticeId: number, noticeName: string) => {
//     try {
//       const ID = localStorage.getItem("useR_ID")?.replace(/\D/g, '') || "0";
//       const collectData = {
//         noticeID: noticeId,
//         user_ID: parseInt(ID),
//         isActive: true,
//       };

//       const response = await api.post(`Notice/GetNoticeMaster`, collectData);

//       if (response.data.isSuccess && response.data.data.length > 0) {
//         const noticeData = response.data.data[0];
//         if (noticeData.noticeDoc) {
//           // Remove the prefix if present (e.g., "data:application/pdf;base64,")
//           const base64Data = noticeData.noticeDoc.split('base64,')[1] || noticeData.noticeDoc;

//           // Convert base64 to Blob
//           const byteCharacters = atob(base64Data);
//           const byteNumbers = new Array(byteCharacters.length);
//           for (let i = 0; i < byteCharacters.length; i++) {
//             byteNumbers[i] = byteCharacters.charCodeAt(i);
//           }
//           const byteArray = new Uint8Array(byteNumbers);
//           const blob = new Blob([byteArray], { type: 'application/pdf' });

//           // Create PDF URL and open in new tab
//           const pdfUrl = URL.createObjectURL(blob);
//           window.open(pdfUrl, '_blank');

//           // Clean up by revoking the object URL
//           setTimeout(() => URL.revokeObjectURL(pdfUrl), 100);
//         } else {
//           console.error("No notice document available");
//         }
//       } else {
//         console.error("Failed to fetch notice document");
//       }
//     } catch (error) {
//       console.error("Error fetching notice document:", error);
//     }
//   };

//   if (isLoading) {
//     return (
//       <Box
//         display="flex"
//         justifyContent="center"
//         alignItems="center"
//         height="100%"
//       >
//         <CircularProgress />
//       </Box>
//     );
//   }

//   return (
//     <Box sx={{ width: "100%", height: "100%" }}>
//       {notices.length === 0 ? (
//         <Typography variant="body1" align="center">
//           No notices available
//         </Typography>
//       ) : (
//         <TableContainer component={Paper} sx={{ maxHeight: "100%", boxShadow: "none" }}>
//           <Table stickyHeader size="small">
//             <TableHead>
//               <TableRow>
//                 <TableCell sx={{ fontWeight: "bold" }}>Sr. No.</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Notice Name</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Notice</TableCell>
//                 <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {notices.map((notice) => (
//                 <TableRow key={notice.noticeID}>
//                   <TableCell>{notice.serialNo}</TableCell>
//                   <TableCell>{notice.notice || "N/A"}</TableCell>
//                   <TableCell>
//                     <Button
//                       variant="text"
//                       color="primary"
//                       onClick={() => handleNoticeClick(notice.noticeID, notice.notice)}
//                       sx={{
//                         textTransform: "none", justifyContent: "flex-start", ":hover": {
//                           textDecoration: "underline",
//                         }
//                       }}
//                     >
//                       {notice.noticeLink}
//                     </Button>
//                   </TableCell>
//                   <TableCell>{notice.formattedNoticeDate}</TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       )}
//     </Box>
//   );
// }